import React, { useState, useEffect } from "react";
import { clearSession } from "../shared/_helpers/session";
import TableComponent from "./TableComponent";
import { useSnackbar } from "notistack";
import StarsIcon from "../styles/StarsIcon";
import { Button, Dialog, DialogContent, DialogTitle, Field as FormField, Input, Page, PageHead, Select } from "./ui";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import Loader from "./Loader";
import { createAdmin } from "../shared/_services/auth.services";
import { updateProfile, blockUnblockUser, checkBlockedStatus, getCountryCode, getAllAdminList } from "../shared/_services/common.services";
import { generateValidPassword } from "../shared/_helpers/generatePassword";
import Pagination from "./Pagination";


const AdminList = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [advisorData, setAdvisorData] = useState([]); //advisorData setAdvisorData
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('add');
  const [selectedAdvisor, setSelectedAdvisor] = useState(null);
  const headers = ["Name", "E-mail", "Phone Number", "Edit", "Block"];
  const [showLoader, setShowLoader] = useState(false);
  const [confirmBlockPopupShow, setConfirmBlockPopupShow] = useState(false);
  const [blockAction, setBlockAction] = useState('');
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);
  const [countrycode, setCountryCodes] = useState([]);


  const confirmBlockPopupClose = () => {
    setConfirmBlockPopupShow(false)
  }

  const openConfirmBlockPopup = (advisor) => {
    setSelectedAdvisor(advisor)
    setBlockAction(advisor.status ? 'block' : 'unblock');
    setConfirmBlockPopupShow(true);
  };


  const getAdminData = async () => {
    setShowLoader(true)
    const response = await getAllAdminList("admin");
    if (response.status === 200) {
      setAdvisorData(response?.data);
    }
    else {
      if (response?.response?.status === 401) {
        clearSession();
        enqueueSnackbar("Your session has been expired.", {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
        navigate('/login');
      }
    }
    setShowLoader(false)
  };

  const checkBlocked = async () => {
    const userId = JSON.parse(localStorage.getItem('currentUser')).user?._id;
    try {
      // Call the API to check the user's blocked status
      const response = await checkBlockedStatus(userId);

      if (response.response ? response?.response?.status == 401 : response.status == 401) {
        clearSession();
        navigate("/login");
      }
      else {
        const { isBlocked, isDeleted } = response.data;

        if (isBlocked || isDeleted) {
          // Clear the currentUser from localStorage
          clearSession();

          // Navigate to the login page
          navigate("/login");
        }
      }
    } catch (error) {
      console.error("Error checking blocked status:", error);
      // Optionally handle errors, e.g., show an error message
    }
  }
  const fetchCountryCodes = async () => {
    const code = await getCountryCode();
    setCountryCodes(code?.data);
  };
  useEffect(() => {
    checkBlocked();
    fetchCountryCodes();
    getAdminData();
  }, []); //eslint-disable-line

  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    }
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .matches(/^[A-Za-z\s]*$/, "Name cannot contain numbers")
      .test("no-leading-space", "Name cannot start with a space", (value) => {
        // Check if value starts with space, return false if it does
        return !value.startsWith(" ");
      }) // Ensure no numbers
      .test("valid-name", "Enter a valid name", (value) => {
        return !!value && value.trim().length > 0; // Ensure at least 1 valid character
      })
      .min(3, "Name must be at least 3 characters")
      .max(30, "Name must be at most 30 characters"),
    email: Yup.string()
      .required("Email is required")
      .test(
        "contains-at-and-dot",
        "Email must contain '@' and '.'",
        (value) => {
          if (!value) return false; // Required validation
          return value.includes("@") && value.includes(".");
        }
      )
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email address"),
    mobile: Yup.string()
      .required("Phone number is required")
      .matches(
        /^(?!.*(\d)\1{2}-(\d)\2{2}-(\d)\3{3})\d{3}-\d{3}-\d{4}$/,
        "Invalid phone number format or repeated digits"
      ),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    setShowLoader(true); // Show loader when form submission starts
    const { name, email, mobile, code } = values;
    const [firstName, ...lastNameArray] = name.split(" ");
    const lastName = lastNameArray.join(" ");
    const randomPassword = generateValidPassword();

    let data;
    setShowModal(false);
    try {
      if (modalType === "add") {
        // No `userType`: the server assigns 'admin' (backend SEC-040). And
        // `country_code`, not `code` — /users/create-admin reads only the
        // former, so the old name was dropped silently.
        data = await createAdmin({
          firstName,
          lastName,
          email,
          mobile,
          password: randomPassword,
          country_code: code,
        });
      } else if (modalType === "edit" && selectedAdvisor) {
        const userId = selectedAdvisor._id;
        const updatedProfile = {
          firstName,
          lastName,
          email,
          mobile,
          code,
        };

        data = await updateProfile(userId, updatedProfile);
      }

      // `/users/create-admin` answers 201 and `/users/update` answers 200, so
      // accept any 2xx. `data` is an axios response on success and — because
      // this service layer returns its errors rather than throwing — an axios
      // error otherwise.
      const status = data?.status ?? data?.response?.status;

      if (status >= 200 && status < 300) {
        enqueueSnackbar(
          modalType === "add" ? "Advisor added successfully" : "Advisor updated successfully",
          { variant: "success" }
        );
        setShowModal(false); // Close modal after successful operation
        getAdminData(); // Refresh the advisor list
      } else {
        // Every non-2xx, not only 401. Creating an admin is super-admin only
        // (backend SEC-051), so a plain admin reaching this screen gets a 403 —
        // which previously matched no branch, closing the modal in silence.
        enqueueSnackbar(
          data?.response?.data?.message ||
          data?.response?.data?.error ||
          `Failed to ${modalType === "add" ? "add" : "update"} advisor`,
          { variant: "error" }
        );
      }
    } catch (error) {
      console.error("Error during submission:", error);
      enqueueSnackbar(`Failed to ${modalType === "add" ? "add" : "update"} advisor`, {
        variant: "error",
      });
    } finally {
      setSubmitting(false); // Enable the "Save" button
      setShowLoader(false); // Hide loader
    }
  };


  const handleAddAdvisorClick = () => {
    setModalType('add');
    setSelectedAdvisor(null);
    setShowModal(true);
  };

  const handleEditAdvisorClick = (advisor) => {
    setModalType('edit');
    setSelectedAdvisor(advisor);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleBlock = async () => {
    setConfirmBlockPopupShow(false);
    setShowLoader(true);
    const advisorId = selectedAdvisor._id;
    const newStatus = !selectedAdvisor.status;

    try {
      await blockUnblockUser({
        userId: advisorId,
        status: newStatus
      });
      getAdminData();
      const action = newStatus ? "unblocked" : "blocked";
      enqueueSnackbar(`Advisor ${action} successfully`, { variant: "success" });
    } catch (error) {
      const action = newStatus ? "unblock" : "block";
      enqueueSnackbar(`Failed to ${action} advisor`, { variant: "error" });
    }
    finally {
      setShowLoader(false);
    }
  };

  //pagination code
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;

  const safeAdvisorData = Array.isArray(advisorData) ? advisorData : [];
  const currentRecords = safeAdvisorData.slice(indexOfFirstRecord, indexOfLastRecord);
  const nPages = Math.ceil(safeAdvisorData.length / recordsPerPage);

  return (
    <Page>
      <PageHead
        title="Admins"
        tools={
          <Button type="button" onClick={handleAddAdvisorClick}>
            Add New Admin
          </Button>
        }
      />

      {showLoader ? <Loader /> :
        <>
          <TableComponent headers={headers} data={currentRecords} test={"advisor"} onEditAdvisorClick={handleEditAdvisorClick} onBlockAdvisorClick={openConfirmBlockPopup} />
          <Pagination
            nPages={nPages}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </>}

      {/* Add or Edit Advisor Modal */}
      <Dialog
        open={showModal}
        onOpenChange={(open) => { if (!open) closeModal(); }}
      >
        <DialogContent
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="tw-mb-4 tw-flex tw-items-start tw-justify-between tw-gap-4">
            <DialogTitle>{modalType === "add" ? "Add New Admin" : "Edit Admin"}</DialogTitle>
            <Button
              variant="icon"
              size="sm"
              iconOnly
              aria-label="Close"
              className="tw-border-solid tw-shrink-0"
              onClick={closeModal}
            >
              <StarsIcon name="x" className="tw-h-4 tw-w-4 tw-shrink-0" />
            </Button>
          </div>
          <Formik
            initialValues={{
              name: selectedAdvisor ? `${selectedAdvisor.firstName} ${selectedAdvisor.lastName}` : "",
              email: selectedAdvisor ? selectedAdvisor.email : "",
              mobile: selectedAdvisor ? selectedAdvisor.mobile : "",
              code: "+1",
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isSubmitting, values, setFieldValue }) => (
              <Form>
                <FormField label="Name" error={errors.name && touched.name ? errors.name : undefined}>
                  <Field
                    as={Input}
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Full Name"
                  />
                </FormField>
                <FormField label="Email address" error={errors.email && touched.email ? errors.email : undefined}>
                  <Field
                    as={Input}
                    type="email"
                    name="email"
                    id="email"
                    placeholder="Email address"
                  />
                </FormField>
                <div className="tw-mb-4 tw-grid tw-gap-2">
                  <label htmlFor="mobile" className="tw-text-sm tw-font-semibold tw-text-text">Phone Number</label>
                  <div className="tw-flex tw-gap-3">
                    <Field as={Select} name="code" disabled wrapperClassName="tw-w-32 tw-shrink-0" aria-label="Country code">
                      {countrycode?.map((item, index) => (
                        <option key={item.code} value={item.code}>
                          ({item.code})
                        </option>
                      ))}
                    </Field>
                    <Field
                      as={Input}
                      type="text"
                      id="mobile"
                      name="mobile"
                      placeholder="Phone Number"
                      invalid={Boolean(errors.mobile && touched.mobile)}
                      className="tw-min-w-0 tw-flex-1"
                      value={formatPhoneNumber(values.mobile)}
                      onChange={e => setFieldValue("mobile", formatPhoneNumber(e.target.value))}
                    />
                  </div>
                  {errors.mobile && touched.mobile && (
                    <p className="tw-m-0 tw-text-sm tw-text-danger">{errors.mobile}</p>
                  )}
                </div>
                <Button type="submit" disabled={isSubmitting} className="tw-mt-2 tw-w-full">
                  Save
                </Button>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* block popup Modal*/}
      <Dialog
        open={confirmBlockPopupShow}
        onOpenChange={(open) => { if (!open) confirmBlockPopupClose(); }}
      >
        <DialogContent
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          className="tw-text-center"
        >
          <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
            <span />
            <Button
              variant="icon"
              size="sm"
              iconOnly
              aria-label="Close"
              className="tw-border-solid tw-shrink-0"
              onClick={confirmBlockPopupClose}
            >
              <StarsIcon name="x" className="tw-h-4 tw-w-4 tw-shrink-0" />
            </Button>
          </div>
          <DialogTitle>{`You are attempting to ${blockAction === 'block' ? 'block' : 'unblock'} ${selectedAdvisor ? selectedAdvisor.firstName : ''}.`}</DialogTitle>
          <p className="tw-m-0 tw-text-md tw-font-bold tw-text-text">Are you sure?</p>
          <div className="tw-mt-6 tw-flex tw-justify-center">
            <Button onClick={handleBlock}>{blockAction === 'block' ? 'Block' : 'Unblock'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Page>
  );
};

export default AdminList;
